import type { ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@savvy-kit/components/dialog';
import { Check } from 'lucide-react';
import { TrackedButton } from 'src/components/tracked';

type UpgradeCustodianMissingDetailsModalProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onReviewFields: () => void;
  readonly onContinueTransferring: () => void;
};

export function UpgradeCustodianMissingDetailsModal({
  open,
  onOpenChange,
  onReviewFields,
  onContinueTransferring,
}: UpgradeCustodianMissingDetailsModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Missing household details</DialogTitle>
          <DialogDescription>
            Some client profiles are incomplete. Adding missing information now will pre-fill repapering forms and
            reduce client effort.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium">When profiles are complete:</p>
          <ul className="flex flex-col gap-2">
            <BenefitItem>Forms auto-populate with existing data</BenefitItem>
            <BenefitItem>Clients only review and sign</BenefitItem>
            <BenefitItem>Fewer follow-ups needed</BenefitItem>
          </ul>
        </div>
        <DialogFooter>
          <TrackedButton
            variant="secondary"
            elementName="upgrade-custodian-missing-details-modal-continue-transferring-button"
            onClick={onContinueTransferring}
          >
            No, continue transferring
          </TrackedButton>
          <TrackedButton
            elementName="upgrade-custodian-missing-details-modal-review-fields-button"
            onClick={onReviewFields}
          >
            Review outstanding fields
          </TrackedButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BenefitItem({ children }: { readonly children: ReactNode }) {
  return (
    <li className="flex items-center gap-2 text-sm">
      <Check className="size-4 shrink-0 text-success" />
      <span>{children}</span>
    </li>
  );
}
